import { useState } from "react";
import { IconButton, Tooltip, Snackbar } from "@mui/material";
import { darkBlue, darkModeTextColor } from "../../../constants/colors";
import { Alert } from "@mui/material";
import { useLocation } from "react-router";
import { useSelector } from "store/useSelector";
import { useCopyClipboard } from "@lokibai/react-use-copy-clipboard";
import { BOX_SHADOW_ALERT, Z_INDEX } from "constants/constants";
import styled from "styled-components/macro";
import queryString, { StringifyOptions } from "query-string";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { LinkIcon } from "svgs";
import { useIsDarkMode } from "store/atoms";

const SnackbarStyles = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 260px;
`;

export default function ButtonShareRange() {
  const { pathname, search } = useLocation();
  // pathname is not guaranteed to be uri encoded (i.e. "Line 1" needs to be encoded as "Line%201" )
  const pathnameStrings = pathname.split("/");
  const uriEncodedPathname = pathnameStrings.reduce(
    (prev, curr) =>
      prev +
      "/" +
      // url may already be encoded; don't double-encode "%"s, only spaces
      (curr.includes(" ") ? encodeURIComponent(curr) : curr)
  );
  const { origin } = window.location;
  const focusRange = useSelector(state => state.legacy.focusRange);
  const query = queryString.parse(search);
  if (focusRange) {
    query.ge = String(focusRange.ge);
    query.lt = String(focusRange.lt);
  }

  const order = ["view", "ge", "lt"];
  const stringifyOptions: StringifyOptions = {
    sort: (itemLeft, itemRight) =>
      order.indexOf(itemLeft) - order.indexOf(itemRight),
  };
  const searchWithRange = queryString.stringify(query, stringifyOptions);

  const pathToShare = focusRange
    ? `${origin}${uriEncodedPathname}${
        searchWithRange ? "?" + searchWithRange : ""
      }`
    : null;
  const [, setCopied] = useCopyClipboard(pathToShare);
  const [copiedValue, setCopiedValue] = useState(null as string | null);
  const isCopiedValueSameAsCurrentValue = copiedValue === pathToShare;
  const showSnackbar = Boolean(copiedValue && isCopiedValueSameAsCurrentValue);

  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();

  if (!focusRange) {
    return null;
  }

  // TECHDEBT: debug navigator share in iOS - error message on cancel
  // const shareData = {
  //   title: `Raven Flight`,
  //   text: `Data for ${new Date(focusRange.ge)} - ${new Date(focusRange.lt)}`,
  //   url: pathToShare
  // };

  // copy share URL to clipboard
  const handleCopyClipboard = () => {
    setCopied(pathToShare);
    setCopiedValue(pathToShare);
    setTimeout(() => setCopiedValue(null), 2500);
  };
  return pathToShare ? (
    <>
      <Tooltip title={translate("Copy_link_to_this_time_range")}>
        <IconButton
          onClick={handleCopyClipboard}
          data-component-name={"Share Range Button"}
          size="large"
        >
          <StyledLinkIcon $isDarkMode={isDarkMode} />
        </IconButton>
      </Tooltip>
      <SuccessToastPopup
        showSnackbar={showSnackbar}
        message={`${translate("Copied_to_clipboard")}!`}
      />
    </>
  ) : null;
}

export const StyledLinkIcon = styled(LinkIcon)<{
  $isDarkMode: boolean;
}>`
  width: 24px;
  height: 24px;
  padding: 0 2px;
  box-sizing: border-box;
  * {
    stroke: none;
    fill: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeTextColor : darkBlue} !important;
  }
`;

export function SuccessToastPopup({
  showSnackbar,
  message,
}: {
  showSnackbar: boolean;
  message: string;
}) {
  const { isTrendsPage } = useGetPageFromPath();
  return (
    <SnackbarStyles className="doNotPrint">
      <Snackbar
        style={{
          zIndex: Z_INDEX.L12 + 999,
          position: "absolute",
          width: 200,
          right: 56,
          top: isTrendsPage ? 6 : 0,
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showSnackbar}
        autoHideDuration={3000}
      >
        <Alert
          style={{
            boxShadow: BOX_SHADOW_ALERT,
          }}
          severity="success"
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarStyles>
  );
}

// use mobile share API
// async function handleShare(shareData: any) {
//   try {
//     return await (navigator as any).share(shareData);
//   } catch (err) {
//     alert(err);
//   }
// }
